import React, { useEffect, useState, useCallback } from 'react';
import { performanceAPI, userAPI } from '../api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Activity, Phone, Users, TrendingUp, DollarSign, Calendar as Cal } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { getSalesRangeDates, localDateBoundaryIso } from '../lib/dateRanges';

const PIE_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6'];
const fmtMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

export const EmployeePerformancePage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user?.id || '');
  const [dateFrom, setDateFrom] = useState(() => getSalesRangeDates('month').from);
  const [dateTo, setDateTo] = useState(() => getSalesRangeDates('month').to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userAPI.getAll().then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const { data } = await performanceAPI.get(selectedUserId, {
        date_from: localDateBoundaryIso(dateFrom),
        date_to: localDateBoundaryIso(dateTo, true)
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
      setData(null);
    } finally { setLoading(false); }
  }, [selectedUserId, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canSelectOthers = user?.role === 'super_admin' || user?.role === 'manager';

  const chartData = data ? [
    { name: 'Calls', value: data.calls.total, color: '#3B82F6' },
    { name: 'Follow-ups', value: data.follow_ups.total, color: '#22C55E' },
    { name: 'Meetings', value: data.meetings, color: '#F59E0B' },
    { name: 'Site Visits', value: data.site_visits, color: '#EC4899' },
    { name: 'Quotations', value: data.quotations, color: '#8B5CF6' },
    { name: 'Sales', value: data.sales, color: '#10B981' }
  ] : [];

  const callBreakdown = data ? [
    { name: 'Connected', value: data.calls.connected },
    { name: 'Not Connected', value: data.calls.not_connected },
    { name: 'Busy', value: data.calls.busy },
    { name: 'Callback', value: data.calls.callback },
    { name: 'Wrong Number', value: data.calls.wrong_number }
  ].filter(x => x.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="performance-container">
      <div>
        <h1 className="text-3xl font-outfit font-semibold">Employee Performance</h1>
        <p className="text-muted-foreground">Calls, follow-ups, meetings, sales & revenue</p>
      </div>

      <div className="clay-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {canSelectOthers ? (
          <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}
            data-testid="performance-user-select"
            className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
            <option value="">Select an employee</option>
            {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</option>)}
          </select>
        ) : (
          <div className="text-sm px-3 py-2 flex items-center gap-2 text-muted-foreground">
            <Users className="h-4 w-4" /> {user?.name}
          </div>
        )}
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" data-testid="performance-date-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" data-testid="performance-date-to" />
      </div>

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       !data ? <div className="clay-card p-8 text-center text-sm text-muted-foreground">Select an employee to view performance.</div> :
       <>
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
           {[
             { l: 'Calls', v: data.calls.total, icon: Phone, tone: 'text-blue-600' },
             { l: 'Meetings', v: data.meetings, icon: Cal, tone: 'text-purple-600' },
             { l: 'Sales', v: data.sales, icon: TrendingUp, tone: 'text-green-600' },
             { l: 'Revenue', v: `₹${(data.revenue || 0).toLocaleString('en-IN')}`, icon: DollarSign, tone: 'text-emerald-600' }
           ].map(k => (
             <div key={k.l} className="clay-card p-4">
               <div className="flex items-center justify-between mb-1">
                 <p className="text-xs text-muted-foreground">{k.l}</p>
                 <k.icon className={`h-4 w-4 ${k.tone}`} />
               </div>
               <p className="text-2xl font-outfit font-semibold">{k.v}</p>
             </div>
           ))}
         </div>

         <div>
           <h2 className="text-xl font-outfit font-medium mb-3">Target & Revenue Performance</h2>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="performance-target-summary">
             {[
               { l: 'Target', v: fmtMoney(data.revenue_target) },
               { l: 'Achieved Revenue', v: fmtMoney(data.revenue) },
               { l: 'Remaining', v: fmtMoney(data.remaining_target) },
               { l: 'Achievement %', v: `${data.target_achievement_pct || 0}%` },
               { l: 'Deals Closed', v: data.sales || 0 },
               { l: 'Expected Pipeline', v: fmtMoney(data.expected_pipeline) },
               { l: 'Average Deal Value', v: fmtMoney(data.average_deal_value) },
               { l: 'Conversion Rate', v: `${data.conversion_rate || 0}%` },
               { l: 'Average Closing Time', v: `${data.average_closing_days || 0} days` }
             ].map(metric => (
               <div key={metric.l} className="clay-card p-4">
                 <p className="text-xs text-muted-foreground">{metric.l}</p>
                 <p className="text-xl font-outfit font-semibold mt-1">{metric.v}</p>
               </div>
             ))}
           </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="clay-card p-4">
             <div className="flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-primary" /><h3 className="font-medium">Activity Breakdown</h3></div>
             <ResponsiveContainer width="100%" height={260}>
               <BarChart data={chartData}>
                 <XAxis dataKey="name" fontSize={11} />
                 <YAxis fontSize={11} />
                 <Tooltip />
                 <Bar dataKey="value">
                   {chartData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                 </Bar>
               </BarChart>
             </ResponsiveContainer>
           </div>
           <div className="clay-card p-4">
             <div className="flex items-center gap-2 mb-3"><Phone className="h-4 w-4 text-primary" /><h3 className="font-medium">Call Outcomes</h3></div>
             {callBreakdown.length ? (
               <ResponsiveContainer width="100%" height={260}>
                 <PieChart>
                   <Pie data={callBreakdown} dataKey="value" nameKey="name" outerRadius={90} label={(e) => e.name}>
                     {callBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                   </Pie>
                   <Tooltip />
                   <Legend />
                 </PieChart>
               </ResponsiveContainer>
             ) : <p className="text-sm text-muted-foreground text-center py-16">No call data</p>}
           </div>
         </div>

         <div className="clay-card p-4">
           <h3 className="font-medium mb-3">Summary</h3>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
             <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
               <p className="text-muted-foreground text-xs">Leads Handled</p>
               <p className="text-lg font-semibold">{data.leads.total}</p>
               <p className="text-xs text-green-600">+{data.leads.positive} positive</p>
               <p className="text-xs text-red-600">-{data.leads.negative} negative</p>
             </div>
             <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
               <p className="text-muted-foreground text-xs">Follow-ups</p>
               <p className="text-lg font-semibold">{data.follow_ups.total}</p>
               <p className="text-xs text-muted-foreground">{data.follow_ups.completed} completed</p>
             </div>
             <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
               <p className="text-muted-foreground text-xs">Quotations Sent</p>
               <p className="text-lg font-semibold">{data.quotations}</p>
             </div>
             <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
               <p className="text-muted-foreground text-xs">Conversion Rate</p>
               <p className="text-lg font-semibold">{data.conversion_rate}%</p>
             </div>
           </div>
         </div>
       </>}
    </div>
  );
};
export default EmployeePerformancePage;
